'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { type Locale, getLocalePath } from '@/lib/i18n';
import { routeFor } from '@/lib/routes';
import { Button } from '@/components/ui/Button';
import styles from './SolutionComparison.module.css';

interface Solution {
  title: string;
  description: string;
  whenToChoose: string;
  scenarios: string[];
  image: string;
  imageAlt: string;
  link: string;
}

interface CommonComparisonLabels {
  moreAbout?: {
    qr?: string;
    kiosk?: string;
    tablet?: string;
    webshop?: string;
    fallback?: string;
  };
  vsBadge?: string;
  demoCtaFallback?: string;
}

interface SolutionComparisonProps {
  /**
   * Centralized UI labels for the component itself (button labels, vs-badge, demo-CTA fallback).
   * Should come from `globalMessages.comparison` (locales/{locale}/global.json :: comparison).
   * Falls back to a built-in EN/NL/DE/SV map if not provided (backward-compat with older callsites).
   */
  commonLabels?: CommonComparisonLabels;
  translations: {
    badge: string;
    title: string;
    subtitle: string;
    qrSolution?: {
      title: string;
      description: string;
      whenToChoose: string;
      scenarios: string[];
      bottomLine?: string;
      cta?: string;
    };
    kioskSolution?: {
      title: string;
      description: string;
      whenToChoose: string;
      scenarios: string[];
      bottomLine?: string;
      cta?: string;
    };
    tabletSolution?: {
      title: string;
      description: string;
      whenToChoose: string;
      scenarios: string[];
      bottomLine?: string;
      cta?: string;
    };
    webshopSolution?: {
      title: string;
      description: string;
      whenToChoose: string;
      scenarios: string[];
      bottomLine?: string;
      cta?: string;
    };
    primarySolution?: {
      title: string;
      description: string;
      whenToChoose: string;
      scenarios: string[];
      bottomLine?: string;
      cta?: string;
    };
    secondarySolution?: {
      title: string;
      description: string;
      whenToChoose: string;
      scenarios: string[];
      bottomLine?: string;
      cta?: string;
    };
    bottomLine?: string;
    cta?: string;
    allIndustriesLink?: {
      text: string;
      description: string;
    };
  };
  primaryType: 'qr' | 'kiosk' | 'tablet' | 'webshop';
  secondaryType: 'qr' | 'kiosk' | 'tablet' | 'webshop';
  locale: Locale;
}

const SOLUTION_SUFFIX: Record<Locale, string> = {
  en: 'Solution',
  nl: 'Oplossing',
  de: 'Lösung',
  sv: 'Lösning',
};

const DEVICE_IMAGES: Record<string, string> = {
  qr: '/images/mockups/qr-mockup.webp',
  kiosk: '/images/mockups/kiosk-mockup.webp',
  tablet: '/images/mockups/tablet-mockup.webp',
  webshop: '/images/webshop-mockups/webshop-anne-max-menu-mobile.webp',
};

export const SolutionComparison: React.FC<SolutionComparisonProps> = ({
  translations,
  commonLabels,
  primaryType,
  secondaryType,
  locale,
}) => {

  const primaryLink = getLocalePath(locale, routeFor(primaryType, locale));
  const secondaryLink = getLocalePath(locale, routeFor(secondaryType, locale));

  // Support both naming conventions and all solution types
  const getSolutionByType = (type: string) => {
    switch (type) {
      case 'qr':
        return translations.qrSolution;
      case 'kiosk':
        return translations.kioskSolution;
      case 'tablet':
        return translations.tabletSolution;
      case 'webshop':
        return translations.webshopSolution;
      default:
        return null;
    }
  };

  const primarySolution = translations.primarySolution || getSolutionByType(primaryType);
  const secondarySolution = translations.secondarySolution || getSolutionByType(secondaryType);

  if (!primarySolution || !secondarySolution) {
    console.error('SolutionComparison: Missing solution data', {
      primaryType,
      secondaryType,
      primarySolution,
      secondarySolution,
      translations
    });
    return null;
  }

  const getButtonLabel = (type: string): string => {
    // Preferred: from commonLabels (locales/{locale}/global.json :: comparison.moreAbout)
    const fromCommon = commonLabels?.moreAbout?.[type as 'qr' | 'kiosk' | 'tablet' | 'webshop'];
    if (fromCommon) return fromCommon;
    if (commonLabels?.moreAbout?.fallback) return commonLabels.moreAbout.fallback;

    // Fallback: built-in map (used when callsite hasn't migrated to commonLabels yet)
    const labels: Record<string, Record<Locale, string>> = {
      qr: { en: 'More about QR', nl: 'Meer over QR', de: 'Mehr über QR', sv: 'Mer om QR' },
      kiosk: { en: 'More about Kiosk', nl: 'Meer over Kiosk', de: 'Mehr über Kiosk', sv: 'Mer om kiosk' },
      tablet: { en: 'More about Tablet', nl: 'Meer over Tablet', de: 'Mehr über Tablet', sv: 'Mer om surfplatta' },
      webshop: { en: 'More about Webshop', nl: 'Meer over Webshop', de: 'Mehr über Webshop', sv: 'Mer om webbshop' },
    };
    const fallbackByLocale: Record<Locale, string> = {
      en: 'Learn more', nl: 'Meer info', de: 'Mehr erfahren', sv: 'Läs mer',
    };
    return labels[type]?.[locale] || fallbackByLocale[locale] || 'Learn more';
  };

  return (
    <section className={styles.comparisonSection}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.badge}>{translations.badge}</span>
          <h2 className={styles.title}>{translations.title}</h2>
          <p className={styles.subtitle}>{translations.subtitle}</p>
        </div>

        {/* Comparison Grid */}
        <div className={styles.comparisonGrid}>
          {/* Primary Solution Card */}
          <div className={styles.solutionCard}>
            <div className={styles.cardInner}>
              {/* Device Image */}
              <div className={styles.deviceWrapper}>
                <div className={styles.deviceGlow} />
                <Image
                  src={DEVICE_IMAGES[primaryType]}
                  alt={`${primarySolution.title} ${SOLUTION_SUFFIX[locale]}`}
                  width={primaryType === 'kiosk' ? 500 : 350}
                  height={primaryType === 'kiosk' ? 800 : 700}
                  className={styles.deviceImage}
                  quality={90}
                  loading="lazy"
                  placeholder="blur"
                  blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjYwMCIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAyKSIvPjwvc3ZnPg=="
                />
              </div>

              {/* Content */}
              <div className={styles.cardContent}>
                <h3 className={styles.solutionTitle}>{primarySolution.title}</h3>
                <p className={styles.solutionDescription}>{primarySolution.description}</p>

                <div className={styles.whenToChoose}>
                  <span className={styles.whenToChooseLabel}>{primarySolution.whenToChoose}</span>
                  <ul className={styles.scenarios}>
                    {primarySolution.scenarios.map((scenario, index) => (
                      <li key={index} className={styles.scenario}>
                        <span className={styles.checkmark} />
                        {scenario}
                      </li>
                    ))}
                  </ul>
                </div>

                <Link href={primaryLink} className={styles.cardLink}>
                  <Button variant="outline" size="md">
                    {getButtonLabel(primaryType)}
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* VS Badge */}
          <div className={styles.vsBadge}>
            <span>{commonLabels?.vsBadge ?? ({ en: 'VS', nl: 'VS', de: 'VS', sv: 'vs' } as Record<Locale, string>)[locale]}</span>
          </div>

          {/* Secondary Solution Card */}
          <div className={styles.solutionCard}>
            <div className={styles.cardInner}>
              {/* Device Image */}
              <div className={styles.deviceWrapper}>
                <div className={styles.deviceGlow} />
                <Image
                  src={DEVICE_IMAGES[secondaryType]}
                  alt={`${secondarySolution.title} ${SOLUTION_SUFFIX[locale]}`}
                  width={secondaryType === 'kiosk' ? 500 : 350}
                  height={secondaryType === 'kiosk' ? 800 : 700}
                  className={styles.deviceImage}
                  quality={90}
                  loading="lazy"
                  placeholder="blur"
                  blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjYwMCIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAyKSIvPjwvc3ZnPg=="
                />
              </div>

              {/* Content */}
              <div className={styles.cardContent}>
                <h3 className={styles.solutionTitle}>{secondarySolution.title}</h3>
                <p className={styles.solutionDescription}>{secondarySolution.description}</p>

                <div className={styles.whenToChoose}>
                  <span className={styles.whenToChooseLabel}>{secondarySolution.whenToChoose}</span>
                  <ul className={styles.scenarios}>
                    {secondarySolution.scenarios.map((scenario, index) => (
                      <li key={index} className={styles.scenario}>
                        <span className={styles.checkmark} />
                        {scenario}
                      </li>
                    ))}
                  </ul>
                </div>

                <Link href={secondaryLink} className={styles.cardLink}>
                  <Button variant="outline" size="md">
                    {getButtonLabel(secondaryType)}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className={styles.bottomCta}>
          <p className={styles.bottomText}>{translations.bottomLine}</p>
          <div className={styles.ctaGroup}>
            <Link href={getLocalePath(locale, routeFor('demo', locale))}>
              <Button variant="primary" size="lg">
                {translations.cta || primarySolution.cta || commonLabels?.demoCtaFallback || ({
                  en: 'Request a demo',
                  nl: 'Demo aanvragen',
                  de: 'Demo anfragen',
                  sv: 'Boka demo',
                } as Record<Locale, string>)[locale]}
              </Button>
            </Link>
          </div>
          {translations.allIndustriesLink && (
            <div className={styles.branchesLinkWrapper}>
              <p className={styles.branchesLinkDescription}>
                {translations.allIndustriesLink.description}
              </p>
              <Link
                href={getLocalePath(locale, routeFor('industries', locale))}
                className={styles.branchesLink}
              >
                {translations.allIndustriesLink.text}
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

